<i18n
  src="@/locales/views/agro_exporter/production/basic_container_export/edit_container.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/container.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/exportation.yml" lang="yaml"></i18n>
<template>
  <go-back-link
    :text="$t('labels.go-back-link')"
    name="ae_production_basic_container_export_show_container_path"
    :params="{
      agro_exporter_process_id: agro_exporter_process_id,
      container_id: container_id
    }"
  />

  <agro-exporter-process-header
    :agro_exporter_process_id="agro_exporter_process_id"
  />

  <alert-danger :errors_data="std_err" />

  <div v-if="load_general_data" class="mt-8">
    <div class="panel">
      <div class="panel-header">
        <h3 class="panel-header-title">
          {{ $t("labels.title") }}
        </h3>
      </div>

      <div class="panel-body">
        <div class="space-y-8 divide-y divide-gray-200" v-if="container">
          <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div class="sm:col-span-3">
              <label :class="['label-form', isInvalidLabel('container_code')]">
                {{ $t("model.container.container_code") }}
              </label>
              <div class="mt-1">
                <input
                  type="text"
                  v-model="container.container_code"
                  :class="['input-text', isInvalid('container_code')]"
                />
              </div>
            </div>
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.container.customer") }}
              </label>
              <div class="mt-1">
                <select-form
                  v-if="customers.length > 0"
                  :options="customers"
                  v-model="container.customer_id"
                />
              </div>
            </div>
          </div>

          <div class="pt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.container.status") }}
              </label>
              <div class="mt-1">
                <container-status-selector v-model="container.status_id" />
              </div>
            </div>
          </div>

          <div class="pt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.container.exportation") }}
              </label>
              <div class="mt-1">
                <select-form
                  v-if="available_exportations.length > 0"
                  :options="available_exportations"
                  v-model="container.exportation_id"
                  label="booking_code"
                />
                <alert-warning
                  v-else
                  :warning_data="{
                    title: $t('labels.not_available_exportations')
                  }"
                />
              </div>
            </div>
          </div>

          <div class="pt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div
              class="sm:col-span-3"
              v-if="producers_organizations.length > 0"
            >
              <label class="label-form">
                {{ $t("model.container.provider") }}
              </label>
              <div class="mt-1">
                <ProducerOrOrganizationSelect
                  :options="producers_organizations"
                  :default_producer_id="container.provider_producer_id"
                  :default_organization_id="container.provider_organization_id"
                  v-model:producer_id="container.provider_producer_id"
                  v-model:organization_id="container.provider_organization_id"
                />
              </div>
            </div>
          </div>
          <div class="pt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.container.packing_house_process") }}
              </label>
              <div class="mt-1">
                <select-form
                  v-if="packing_house_processes.length > 1"
                  :options="packing_house_processes"
                  v-model="container.packing_house_process_id"
                />
                <div
                  class="content-data"
                  v-else-if="packing_house_processes.length == 1"
                >
                  {{ packing_house_processes[0].name }}
                </div>
              </div>
            </div>

            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.container.load_date") }}
              </label>
              <div class="mt-1">
                <date-form v-model="container.load_date" />
              </div>
            </div>
          </div>
          <div class="pt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.container.waybill") }}
              </label>
              <div class="mt-1">
                <input
                  type="text"
                  v-model="container.waybill"
                  class="input-text"
                />
              </div>
            </div>

            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.container.exporter_process_brand") }}
              </label>
              <div class="mt-1 grid grid-cols-1 sm:grid-cols-8">
                <div class="sm:col-span-2">
                  <dt class="mt-1">
                    <SwitchGroup as="div" class="flex items-center">
                      <Switch
                        v-model="container.fixed_brand"
                        :class="[
                          container.fixed_brand ? 'bg-lime-600' : 'bg-gray-200',
                          'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                        ]"
                      >
                        <span
                          aria-hidden="true"
                          :class="[
                            container.fixed_brand
                              ? 'translate-x-5'
                              : 'translate-x-0',
                            'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
                          ]"
                        />
                      </Switch>
                      <SwitchLabel as="span" class="ml-3">
                        <span class="label-form">
                          {{ $t("model.container.fixed_brand") }}
                        </span>
                      </SwitchLabel>
                    </SwitchGroup>
                  </dt>
                </div>
                <div class="sm:col-span-6" v-if="container.fixed_brand">
                  <select-form
                    v-if="brands.length > 1"
                    :options="brands"
                    v-model="container.exporter_process_brand_id"
                  />
                  <div class="content-data" v-else-if="brands.length == 1">
                    {{ brands[0].name }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            class="pt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6"
            v-if="container"
          >
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.container.cols_package_groups") }}
              </label>
              <div class="mt-1">
                <input
                  type="text"
                  v-model="container.cols_package_groups"
                  class="input-text"
                />
              </div>
            </div>
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.container.rows_package_groups") }}
              </label>
              <div class="mt-1">
                <input
                  type="text"
                  v-model="container.rows_package_groups"
                  class="input-text"
                />
              </div>
            </div>
          </div>

          <div class="pt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div class="sm:col-span-6">
              <label class="label-form">
                {{ $t("model.container.private_comments") }}
                <em class="text-sm text-green-800 bg-green-100 p-1 rounded-md">
                  {{ $t("model.container.describe_private_comments") }}
                </em>
              </label>
              <div class="mt-1">
                <textarea
                  v-model="container.private_comments"
                  rows="3"
                  class="input-text-area"
                />
              </div>
            </div>
          </div>

          <div class="pt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <switch-form
              class="sm:col-span-6"
              v-model="add_comment"
              :label="$t('labels.edit_comment_label')"
            />
            <div class="sm:col-span-6">
              <label class="label-form">
                {{ $t("model.container.comments") }}
                <em class="text-sm text-red-800 bg-red-100 p-1 rounded-md">
                  {{ $t("model.container.describe_comments") }}
                </em>
              </label>
              <div class="mt-1">
                <textarea
                  v-model="container.comments"
                  rows="3"
                  :disabled="!add_comment"
                  :class="['input-text-area', add_comment ? '' : 'bg-gray-100']"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="panel-footer">
        <div class="panel-footer-actions">
          <white-button
            @click.prevent="goToShowContainer()"
            :label="$t('labels.cancel_button')"
          />
          <span class="space-actions"></span>
          <success-button
            v-if="!proc_save_button"
            @click.prevent="updateContainer()"
            :label="$t('labels.save_button')"
          />
          <success-button-processing v-else :label="$t('labels.save_button')" />
        </div>
      </div>
    </div>
  </div>
  <loading-message v-else />
</template>

<script>
import moment from "moment";
import BasicContainerExportQRequester from "@/views/agro_exporter/requesters/production/basic_container_export/query.js";
import BasicContainerExportMCRequester from "@/views/agro_exporter/requesters/production/basic_container_export/manage_container.js";
import { Switch, SwitchGroup, SwitchLabel } from "@headlessui/vue";
import AgroExporterProcessHeader from "@/components/views/agro_exporter/production/basic_container_export/AgroExporterProcessHeader.vue";
import ContainerStatusSelector from "@/components/views/agro_exporter/production/basic_container_export/ContainerStatusSelector.vue";
import ProducerOrOrganizationSelect from "@/components/views/agro_exporter/production/basic_container_export/ProducerOrOrganizationSelect.vue";

export default {
  name: "AEProductionBasicContainerExportNewContainer",
  components: {
    AgroExporterProcessHeader,
    ContainerStatusSelector,
    Switch,
    SwitchGroup,
    SwitchLabel,
    ProducerOrOrganizationSelect
  },
  props: ["agro_exporter_process_id", "container_id", "agro_exporter_id"],
  data() {
    return {
      load_general_data: false,
      proc_save_button: false,
      container: null,
      available_exportations: [],
      container_statuses: [],
      customers: [],
      packing_house_processes: [],
      brands: [],
      add_comment: false,
      producers_organizations: [],
      provider_producer: {},
      provider_organization: {},
      original_packing_house_process_id: null
    };
  },
  methods: {
    fetchData: async function () {
      await this.fetchContainer();
      await this.fetchAvailableExportations();
      await this.fetchCustomers();
      await this.fetchExporterProcessBrands();
      await this.fetchPackingHouseProcesses();
      await this.fetchProducersOrganizations();
      this.load_general_data = true;
    },
    fetchContainer: async function () {
      let response = await BasicContainerExportMCRequester.fetchContainer(
        this.container_id
      );
      this.container = response.data;
      this.original_packing_house_process_id =
        response.data.packing_house_process_id;
    },
    fetchAvailableExportations: async function () {
      let response =
        await BasicContainerExportMCRequester.fetchAvailableToAssignExportations(
          this.agro_exporter_process_id
        );
      this.available_exportations = response.data;
    },
    fetchCustomers: async function () {
      let response = await BasicContainerExportQRequester.fetchCustomers(
        this.agro_exporter_process_id
      );
      this.customers = response.data;
    },
    fetchExporterProcessBrands: async function () {
      let response =
        await BasicContainerExportQRequester.fetchExporterProcessBrands(
          this.agro_exporter_process_id
        );
      this.brands = response.data;
      if (this.brands.length == 1)
        this.container.exporter_process_brand_id = this.brands[0].id;
    },
    fetchPackingHouseProcesses: async function () {
      let response =
        await BasicContainerExportQRequester.fetchPackingHouseProcesses(
          this.agro_exporter_process_id
        );
      this.packing_house_processes = response.data;
    },
    fetchProducersOrganizations: async function () {
      let response =
        await BasicContainerExportQRequester.fetchProducersOrganizations(
          this.agro_exporter_process_id
        );
      this.producers_organizations = response.data;
    },
    goToShowContainer: function () {
      this.$router.push({
        name: "ae_production_basic_container_export_show_container_path",
        params: {
          agro_exporter_process_id: this.agro_exporter_process_id,
          container_id: this.container_id
        }
      });
    },
    filterPackingHouseProcessByProvider: function (entity, provider_id) {
      return this.packing_house_processes.filter(
        packing_house_process =>
          packing_house_process.entity_id ==
          this.producers_organizations.find(
            producer_organization =>
              producer_organization[entity] == provider_id
          ).entity_id
      );
    },
    updateContainer: async function () {
      this.proc_save_button = true;
      try {
        let response = await BasicContainerExportMCRequester.updateContainer(
          this.container_id,
          {
            container_code: this.container.container_code,
            customer_id: this.container.customer_id,
            load_date: this.container.load_date,
            exporter_process_brand_id: this.container.exporter_process_brand_id,
            fixed_brand: this.container.fixed_brand,
            packing_house_process_id: this.container.packing_house_process_id,
            rows_package_groups: this.container.rows_package_groups,
            cols_package_groups: this.container.cols_package_groups,
            waybill: this.container.waybill,
            comments: this.container.comments,
            exportation_id: this.container.exportation_id,
            status_id: this.container.status_id,
            private_comments: this.container.private_comments,
            provider_producer_id: this.container.provider_producer_id,
            provider_organization_id: this.container.provider_organization_id
          }
        );
        this.registerSuccessNotification({
          title: response.data.success_message
        });
        this.goToShowContainer();
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields
          });
        }
      }
      this.proc_save_button = false;
    }
  },
  computed: {
    load_date_formatted: function () {
      return moment(this.container.load_date).format("YYYY-MM-DD");
    }
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          agro_exporter_id: this.agro_exporter_id
        }
      }).then(() => this.fetchData())
    );
  },
  watch: {
    "container.provider_producer_id": function () {
      let packing_house_processes = this.filterPackingHouseProcessByProvider(
        "producer_id",
        this.container.provider_producer_id
      );
      if (packing_house_processes.length > 0)
        this.container.packing_house_process_id = packing_house_processes[0].id;
    },
    "container.provider_organization_id": function () {
      let packing_house_processes = this.filterPackingHouseProcessByProvider(
        "organization_id",
        this.container.provider_organization_id
      );
      if (packing_house_processes.length > 0)
        this.container.packing_house_process_id = packing_house_processes[0].id;
    }
  }
};
</script>
